import { siteConfig } from '@/constants/site'

/**
 * THE WORDS A SEARCH RESULT AND A SHARE PREVIEW SHOW.
 *
 * Pure and browser-safe. Every public page builds its title and description
 * through here so the site reads as one product rather than as thirty pages
 * that each invented their own phrasing.
 *
 * THE LANGUAGE RULE, which outranks every optimisation below. A listing's
 * words belong to the person who posted them. If a seller wrote in Albanian,
 * the title, the description and the share preview stay in Albanian; if they
 * wrote in English they stay in English; a post that mixes the two keeps the
 * mix. NOTHING HERE TRANSLATES, and nothing here restates a fact in different
 * words — the only operations performed on a seller's text are removing
 * formatting noise and cutting it short. This is the same rule
 * `@/lib/telegram/ingest/fidelity` enforces at ingestion, applied at the point
 * the text is published to a crawler.
 */

/** Google renders roughly this much of a description before truncating it. */
export const DESCRIPTION_LIMIT = 160

/** The page-name half of a title. `<Seo>` appends " - Bazio" to it. */
export const TITLE_LIMIT = 70

/**
 * `Cc` is every control character and `Cf` every format character — the
 * zero-width joiners and byte-order marks that ride along invisibly in text
 * pasted out of a phone. Unicode property escapes are used rather than a
 * literal range so the source of this file stays readable and printable.
 */
const CONTROL = /\p{Cc}/gu
const INVISIBLE = /\p{Cf}/gu

/**
 * Formatting noise out, meaning untouched.
 *
 * Line breaks become spaces, runs of whitespace collapse, and a row of
 * exclamation marks or dashes used as a divider collapses to one character. No
 * word is changed, removed or reordered, so the text still says exactly what
 * the seller said.
 */
export const cleanMetaText = (input: string | null | undefined): string => (input ?? '')
    .replace(CONTROL, ' ')
    .replace(INVISIBLE, '')
    .replace(/([!?.,\-_=*~•·])\1{2,}/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim()

/**
 * Cuts at a word boundary and says so with an ellipsis.
 *
 * Cutting mid-word looks like a bug in a search result, and in a language the
 * reader speaks and the writer does not it looks like a mistranslation.
 */
export const truncateMeta = (input: string, limit: number): string => {
    const value = input.trim()

    if (value.length <= limit) return value

    const cut = value.slice(0, limit - 1)
    const lastSpace = cut.lastIndexOf(' ')
    const stem = (lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.\-–—]+$/, '')

    return `${stem}…`
}

/** Clean, then cut. The one function page code should reach for. */
export const metaDescription = (input: string | null | undefined, limit = DESCRIPTION_LIMIT): string =>
    truncateMeta(cleanMetaText(input), limit)

export type ListingTitleFacts = {
    title: string
    /** The formatted price exactly as the page shows it, or null. */
    priceLabel?: string | null
    price?: number | null
    priceType?: string | null
    /** The marketplace the listing lives in. */
    communityName?: string | null
}

const hasRealPrice = (facts: ListingTitleFacts): boolean =>
    typeof facts.price === 'number' && facts.priceType !== 'contact' && Boolean(facts.priceLabel)

/**
 * A listing's page name, price included when there is one.
 *
 * The site-wide title contract is `{page name} - {site name}` with a hyphen and
 * the site name stated ONCE, built by `<Seo>`. So the marketplace and the price
 * ride inside the page name rather than as extra pipe-separated segments, and
 * both are dropped, marketplace first, when the result would be too long to
 * survive a search result anyway.
 *
 * The listing's own words are never rewritten to make room; only the parts THIS
 * function added are removed.
 */
export const listingSeoTitle = (facts: ListingTitleFacts): string => {
    const title = cleanMetaText(facts.title) || 'Listing'
    const priced = hasRealPrice(facts) ? `${title} for ${facts.priceLabel}` : title
    const withMarket = facts.communityName ? `${priced} · ${facts.communityName}` : priced

    if (withMarket.length <= TITLE_LIMIT) return withMarket
    if (priced.length <= TITLE_LIMIT) return priced

    return truncateMeta(title, TITLE_LIMIT)
}

/**
 * A listing's description, in the seller's own language.
 *
 * When the seller wrote nothing usable, the fallback states only facts already
 * on the page — what it is, where it is listed — and never invents a condition,
 * a delivery promise or a sales pitch. Note it is built in ENGLISH, which is
 * why it is a last resort: the seller's own sentence, however short, is always
 * the better answer.
 */
export const listingSeoDescription = (facts: {
    title: string
    description: string
    communityName?: string | null
    location?: string | null
}): string => {
    const own = metaDescription(facts.description)

    if (own.length >= 40) return own

    const where = cleanMetaText(facts.communityName ?? '') || siteConfig.name
    const place = facts.location ? ` in ${cleanMetaText(facts.location)}` : ''

    return metaDescription(`${cleanMetaText(facts.title)}${place}. Listed on ${where}.`)
}

/**
 * A marketplace's description, its own words first.
 *
 * The counts are stated only when the caller resolved them from the database,
 * because "0 active listings" reads as a dead marketplace and an unresolved
 * count must not be printed as one.
 */
export const marketplaceSeoDescription = (facts: {
    name: string
    description?: string | null
    listingCount?: number | null
}): string => {
    const own = cleanMetaText(facts.description)

    if (own.length >= 40) return truncateMeta(own, DESCRIPTION_LIMIT)

    const stock = typeof facts.listingCount === 'number' && facts.listingCount > 0
        ? ` ${facts.listingCount} active ${facts.listingCount === 1 ? 'listing' : 'listings'}.`
        : ''

    return metaDescription(`${facts.name} is a community marketplace on ${siteConfig.name}.${stock} Browse listings and message sellers on Telegram.`)
}

/**
 * The `robots` directive.
 *
 * The positive case is not simply "index": `max-image-preview:large` is what
 * lets Google show the listing photo at full width, and it has to be asked for.
 * The negative case pairs `noindex` with `nofollow` so a private or expired
 * page does not pass authority into pages that should not be crawled either.
 */
export const robotsDirective = (noindex: boolean | undefined): string => (
    noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
)
